import { AnySetupType } from '../deployment/cartTechnicalCodes.component';
import endpoint from '../util/endpoint';
import { storeManagerEndpoints } from '@/lib/endpoints';
import { duration } from '@whiz-cart/node-shared/duration';
import { CartDefect, CartTicketCreateRequestV2 } from '@whiz-cart/node-shared/models/cart/cartDefect';
import { CartGenerations } from '@whiz-cart/node-shared/models/cart/cartGenerations';
import { CartGlobalViewModel, CartLocationModel } from '@whiz-cart/node-shared/models/cart/cartGlobalViewModel';
import { CartTicketStatus } from '@whiz-cart/node-shared/models/cart/cartTicket';
import { OverviewCart } from '@whiz-cart/node-shared/models/cart/overviewCart';
import { service } from '@whiz-cart/node-shared/service/service';
import { createCache } from 'cross-state';
import { isToday } from 'date-fns/esm';
import _ from 'lodash';
import { Action } from 'schummar-state/react';
import { z } from 'zod';

export type Environment = 'dev' | 'test' | 'prod';

export type ListedCart = OverviewCart & {
    hasBeenSeenToday: boolean;
};

export default service(
    'cartService',
    class CartService {
        getList = new Action(
            async ({ storeGuid }: { storeGuid?: string }): Promise<ListedCart[]> => {
                const chunkSize = 1000;
                let skip = 0;
                const carts: any[] = [];
                let totalItemCount = Infinity;

                while (carts.length < totalItemCount) {
                    const chunk: { items: any[]; totalItemCount: number } = await endpoint('storeManager.getCarts', {
                        storeGuid: storeGuid ?? '',
                    }).get({
                        params: { skip, limit: chunkSize },
                    });
                    skip += chunkSize;
                    carts.push(...OverviewCart.array().parse(chunk.items));
                    totalItemCount = chunk.totalItemCount;
                }

                return carts.map((cart) => ({
                    ...cart,
                    hasBeenSeenToday: !!cart.lastSeen && isToday(new Date(cart.lastSeen)),
                }));
            },
            { invalidateAfter: duration({ minutes: 1 }) },
        );

        get = new Action(
            async ({ storeGuid, cartId }: { storeGuid: string; cartId?: string }) => {
                const response: { items: any[] } = await endpoint('storeManager.getCarts', { storeGuid }).get({
                    params: { cartId, limit: 1 },
                });

                if (!response.items[0]) {
                    throw new Error('Cart not found');
                }

                return response.items[0];
            },
            { invalidateAfter: duration({ minutes: 1 }) },
        );

        getLocations = new Action(
            async ({
                storeGuid,
                count = 100,
                from = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
                to = new Date().toISOString(),
            }: {
                storeGuid: string;
                count?: number;
                from?: string;
                to?: string;
            }) => {
                const response = await endpoint('storeManager.getCartsLocations', { storeGuid, count, from, to }).get({});

                return response as CartLocationModel[];
            },
            { invalidateAfter: duration({ minutes: 10 }) },
        );

        async getTotal(storeGuid: string, cartId: string) {
            const response = await endpoint('storeManager.getCarts', { storeGuid }).get<any>({ params: { cartId, limit: 1000 } });
            return response.totalItemCount as number;
        }

        async updateState(cartGuid: string, state: { isDisabled: boolean; isMissing: boolean } & any) {
            const updatedCart = await endpoint('storeManager.updateCartState').post<any>({ cartGuid, ...state });

            this.getList.invalidateCache({ storeGuid: updatedCart.storeGuid });
            this.get.setCache({ storeGuid: updatedCart.storeGuid, cartId: updatedCart.cartId }, updatedCart);
            this.listCartDefects({ storeGuid: updatedCart.storeGuid, cartGuid: updatedCart.cartGuid }).invalidate();
        }

        getCartState(cartGuid: string) {
            return endpoint('storeManager.getCartState', { cartGuid }).get();
        }

        cartSetupInit = new Action(
            async (storeGuid: string) => {
                return endpoint('storeManager.cartSetupInit', { storeGuid }).get<{ setupGuid: string; setupUrl: string }>();
            },
            {
                invalidateAfter: duration({ minutes: 5 }),
            },
        );

        /**
         * Generates values as JWT
         * @param {*} values
         * @param {*} values.setupType
         * @param {*} values.data base64 encoded data, different depending on setupType
         */
        getCodeForSetupService = createCache(
            async ({
                storeGuid,
                ...params
            }: {
                storeGuid: string;
            } & (
                | {
                      kind: 'storeWifi';
                  }
                | {
                      kind?: 'default';
                      type: AnySetupType;
                      data?: string;
                  }
            )) => {
                if (params.kind === 'storeWifi') {
                    return storeManagerEndpoints.cart.setup.generateStoreWifiCodeForSetupService({ storeGuid }).fetch();
                }

                const { type, data } = params;
                if (type === undefined) return;
                return storeManagerEndpoints.cart.setup.generateCodeForSetupService({ storeGuid }).fetch({
                    sub: type,
                    data: data ?? '',
                });
            },
            { invalidateAfter: 14 * 60 * 1000 },
        );

        listCartDefects = createCache(
            async ({ storeGuid, cartGuid }: { storeGuid: string; cartGuid: string }) => {
                const response = await storeManagerEndpoints.cart.tickets.listCartDefects({ storeGuid }).fetch({
                    query: {
                        cartGuid,
                        limit: 100,
                    },
                });
                return _.orderBy(response.items, 'createdOn', 'desc');
            },
            { invalidateAfter: { minutes: 1 } },
        );

        async createCartDefect(defect: CartTicketCreateRequestV2) {
            const ticket = await storeManagerEndpoints.cart.tickets.createCartDefect().fetch(defect);

            this.getList.invalidateCache({ storeGuid: ticket.storeGuid ?? '' });
            this.get.invalidateCacheAll();
            this.listCartDefects({ storeGuid: ticket.storeGuid ?? '', cartGuid: ticket.cartGuid ?? '' }).invalidate();
            this.listCartAndStoreDefects(ticket.storeGuid ?? '').invalidate();

            return ticket;
        }

        async cancelCartDefect(cartTicketGuid: string) {
            const cancelledTicket = await storeManagerEndpoints.cart.tickets.cancelCartDefect().fetch({ cartTicketGuid });

            this.getList.invalidateCacheAll();
            this.get.invalidateCacheAll();
            this.listCartDefects.invalidateAll();
            this.listCartAndStoreDefects.invalidateAll();

            return cancelledTicket;
        }

        listCartAndStoreDefects = createCache(async (storeGuid?: string) => {
            return storeManagerEndpoints.cart.tickets.listCartAndStoreDefects().fetch({ query: { storeGuid } });
        });

        getCartDefectTicketSettings = createCache(
            (storeGuid: string) => {
                return storeManagerEndpoints.cart.tickets.getTicketSettings({ storeGuid }).fetch();
            },
            {
                clearOnInvalidate: false,
                invalidateAfter: {
                    hours: 1,
                },
            },
        );

        async getCartGpsLocations({ storeGuid, cartGuid }: { storeGuid: string; cartGuid: string }) {
            return endpoint('storeManager.getCartGpsLocations', { storeGuid, cartGuid }).post({});
        }

        async deleteCart(cartGuid: string, cartId: string, storeGuid?: string) {
            try {
                const result = await endpoint('storeManager.deleteCart', { cartGuid }).delete({});

                if (storeGuid) {
                    this.getList.invalidateCache({ storeGuid });
                }

                return result;
            } catch (error) {
                console.error(`Could not delete cart: ${cartId}`);
                throw error;
            }
        }

        async getGlobalCarts(skip: number, limit: number) {
            try {
                const result = await endpoint('storeManager.getGlobalCarts').get({ params: { skip, limit } });
                return CartGlobalViewModel.array().parse(result);
            } catch (e) {
                console.error(e);
                return [];
            }
        }

        async getGlobalCartDetails({ framenumber, cartGuid }: { framenumber: string; cartGuid: string }) {
            if (framenumber != null && framenumber != undefined) {
                return await this.getGlobalCartDetailsByFramenumber(framenumber);
            } else {
                return await this.getGlobalCartDetailsByGuid(cartGuid);
            }
        }

        async getGlobalCartDetailsByFramenumber(framenumber: string) {
            return await endpoint('storeManager.getGlobalCartDetailsByFramenumber').get({ params: { framenumber } });
        }

        async getGlobalCartDetailsByGuid(cartGuid: string) {
            return await endpoint('storeManager.getGlobalCartDetailsByGuid').get({ params: { cartGuid } });
        }

        /**
         * Returns the type name of the cart (premium/light/express)
         * @param {object} values
         * @param {string} values.hardwareRevision HardwareRevision
         * @param {string} values.frameNumber HardwareRevision
         * @param {boolean} values.isMobile Mobile
         * @param {boolean} values.premiumExplicitlyNamed If true, returns "Premium" if the cart is of this type, otherwise an empty string. Premium is the expected default.
         */
        getTypeName({
            hardwareRevision,
            frameNumber,
            isMobile = false,
            premiumExplicitlyNamed = false,
        }: {
            hardwareRevision?: string;
            frameNumber?: string;
            isMobile?: boolean;
            premiumExplicitlyNamed?: boolean;
        }) {
            const hardwareRevisionOrFramenumber = hardwareRevision ?? frameNumber;
            if (isMobile) {
                return 'Express';
            }
            if (!hardwareRevisionOrFramenumber) {
                return '';
            }
            if (this.isEsLight({ hardwareRevision, frameNumber })) {
                return 'Light';
            } else {
                return premiumExplicitlyNamed ? 'Premium' : '';
            }
        }

        /**
         * True, if the hardwareRevision or FrameNumber indicate that the Cart is a ES Light Cart
         */
        isEsLight(cart: { hardwareRevision?: string | null; frameNumber?: string | null }) {
            const isHardwareRevisionEsLight = typeof cart.hardwareRevision === 'string' && cart.hardwareRevision.includes('EL');
            const isFrameNumberEsLight =
                typeof cart.frameNumber === 'string' && (/^.{4}102/.test(cart.frameNumber) || cart.frameNumber.startsWith('EL'));

            return isHardwareRevisionEsLight || isFrameNumberEsLight;
        }

        listCartGenerations = new Action(
            async () => {
                const response = await endpoint('storeManager.listCartGenerations').get();
                const parsed = z.array(CartGenerations).parse(response);
                return parsed.map((item) => ({ ...item, id: item.productNumber }));
            },
            { invalidateAfter: duration({ minutes: 1 }) },
        );

        async handleProductNumberChange(data: Omit<CartGenerations, 'createdOn' | 'createdBy' | 'modifiedOn' | 'modifiedBy'>) {
            if (data.id && data.id != data.productNumber) {
                // workaround: product number is handled as id, so to change the product number
                // the previous one has to be removed and a new one created
                // this can be removed if the backend uses an id itself and recognizes when the productNumber changes
                await this.deleteCartGeneration(data.id);
            }
        }

        async upsertCartGenerations(data: Omit<CartGenerations, 'createdOn' | 'createdBy' | 'modifiedOn' | 'modifiedBy'>) {
            await this.handleProductNumberChange(data);
            const response = await endpoint('storeManager.upsertCartGenerations').post(data);
            return CartGenerations.parse(response);
        }

        async deleteCartGeneration(productNumber: string) {
            return await endpoint('storeManager.deleteCartGenerations', { productNumber }).delete();
        }
    },
);

export function isCartDefectOpen(defect: CartDefect) {
    return defect.externalTicket?.status !== CartTicketStatus.Resolved && defect.externalTicket?.status !== CartTicketStatus.Closed;
}
